import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import LeendrLogoMark from './LeendrLogoMark';
import { MagnifyingGlass, List, X, User, SignOut, Plus, ChatCircle, Gift, UserCircle, Heart, ShieldCheck, Bell } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
import TrustBadge from './TrustBadge';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { primary: '#534AB7', dark: '#26215C', accent: '#AFA9EC', bg: '#EEEDFE', white: '#FFFFFF', border: 'rgba(175,169,236,0.25)' };

export default function LeendrHeader() {
  const { user, logout } = useAuth();
  const { lang, switchLang } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const labels = lang === 'de'
    ? { discover: 'Entdecken', rent: 'Vermieten', dashboard: 'Dashboard', profile: 'Profil', messages: 'Nachrichten', referral: 'Empfehlungen', favorites: 'Favoriten', priceAlerts: 'Preis-Alarme', admin: 'Admin', logout: 'Abmelden', login: 'Anmelden', register: 'Registrieren', search: 'Suche nach Gegenständen...' }
    : { discover: 'Discover', rent: 'Rent out', dashboard: 'Dashboard', profile: 'Profile', messages: 'Messages', referral: 'Referrals', favorites: 'Favorites', priceAlerts: 'Price alerts', admin: 'Admin', logout: 'Sign out', login: 'Log in', register: 'Sign up', search: 'Search for items...' };

  useEffect(() => {
    if (user && user !== false) {
      const fetchUnread = () => axios.get(`${API}/messages/unread-count`, { withCredentials: true }).then(r => setUnreadCount(r.data.unread_count)).catch(() => {});
      fetchUnread();
      const interval = setInterval(fetchUnread, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) { navigate(`/listings?search=${encodeURIComponent(searchQuery.trim())}`); setSearchQuery(''); setMobileOpen(false); }
  };
  const handleLogout = async () => { await logout(); navigate('/'); };

  const LANGS = ['de', 'en', 'fr', 'es', 'it', 'uk', 'ar'];

  return (
    <header className="sticky top-0 z-50" style={{ backgroundColor: 'rgba(238,237,254,0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: `0.5px solid ${C.border}` }}>
      <div className="max-w-6xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <LeendrLogoMark size={34} />
          <span className="text-xl font-bold tracking-tight hidden sm:block" style={{ color: C.dark, fontFamily: "'DM Sans', sans-serif" }}>leendr</span>
        </Link>

        {/* Search */}
        <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2" size={18} style={{ color: C.accent }} />
            <input data-testid="header-search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={labels.search}
              className="w-full pl-10 pr-4 py-2 text-sm outline-none focus:ring-2 rounded-full" style={{ backgroundColor: C.white, border: `0.5px solid ${C.border}`, color: C.dark, '--tw-ring-color': C.accent }} />
          </div>
        </form>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-2">
          <div className="flex items-center rounded-full overflow-hidden mr-1" style={{ border: `0.5px solid ${C.border}` }}>
            {LANGS.map(l => (
              <button key={l} onClick={() => switchLang(l)} data-testid={`lang-${l}`} className="px-1.5 py-1 text-[10px] font-bold tracking-wide transition-all"
                style={{ backgroundColor: lang === l ? C.primary : 'transparent', color: lang === l ? C.white : C.accent }}>{l.toUpperCase()}</button>
            ))}
          </div>

          <Link to="/listings" data-testid="browse-link">
            <Button variant="ghost" className="text-sm font-medium rounded-full hover:opacity-70" style={{ color: C.dark }}>{labels.discover}</Button>
          </Link>

          {user && user !== false ? (
            <>
              <NotificationBell />
              <Link to="/messages" data-testid="messages-link" className="relative p-2 hover:opacity-70 transition-opacity" style={{ color: C.dark }}>
                <ChatCircle size={22} />
                {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold" style={{ backgroundColor: C.primary, color: C.white }}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </Link>
              <Link to="/create-listing" data-testid="create-listing-link">
                <Button className="text-sm rounded-full gap-1" style={{ backgroundColor: C.primary, color: C.white }}>
                  <Plus weight="bold" size={16} /> {labels.rent}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:opacity-80 transition-opacity" style={{ border: `0.5px solid ${C.border}` }}>
                    {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <User size={20} style={{ color: C.accent }} />}
                    <span className="text-sm font-medium max-w-[100px] truncate" style={{ color: C.dark }}>{user.name}</span>
                    {user.verified && <TrustBadge size="xs" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl" style={{ border: `0.5px solid ${C.border}` }}>
                  <DropdownMenuItem data-testid="dashboard-link" onClick={() => navigate('/dashboard')}>{labels.dashboard}</DropdownMenuItem>
                  {user.role === 'admin' && <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link"><ShieldCheck size={16} className="mr-2" style={{ color: C.primary }} /> {labels.admin}</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => navigate('/profile')}><UserCircle size={16} className="mr-2" /> {labels.profile}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/messages')}><ChatCircle size={16} className="mr-2" /> {labels.messages}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referral')}><Gift size={16} className="mr-2" /> {labels.referral}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="favorites-link"><Heart size={16} className="mr-2" /> {labels.favorites}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/price-alerts')} data-testid="price-alerts-link"><Bell size={16} className="mr-2" /> {labels.priceAlerts}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-500"><SignOut size={16} className="mr-2" /> {labels.logout}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="login-link"><Button variant="ghost" className="text-sm rounded-full" style={{ color: C.primary }}>{labels.login}</Button></Link>
              <Link to="/register" data-testid="register-link"><Button className="text-sm rounded-full" style={{ backgroundColor: C.primary, color: C.white }}>{labels.register}</Button></Link>
            </div>
          )}
        </div>

        {/* Mobile */}
        <button data-testid="mobile-menu-btn" className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} style={{ color: C.dark }}>
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden py-4 px-5 space-y-3 animate-fade-in" style={{ borderTop: `0.5px solid ${C.border}` }}>
          <form onSubmit={handleSearch} className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2" size={18} style={{ color: C.accent }} />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={labels.search} className="w-full pl-10 pr-4 py-2 text-sm rounded-full" style={{ border: `0.5px solid ${C.border}` }} />
          </form>
          <Link to="/listings" className="block py-2 text-sm font-medium" style={{ color: C.dark }} onClick={() => setMobileOpen(false)}>{labels.discover}</Link>
          {user && user !== false ? (
            <>
              <Link to="/dashboard" className="block py-2 text-sm font-medium" style={{ color: C.dark }} onClick={() => setMobileOpen(false)}>{labels.dashboard}</Link>
              <Link to="/messages" className="block py-2 text-sm font-medium" style={{ color: C.dark }} onClick={() => setMobileOpen(false)}>{labels.messages}</Link>
              <Link to="/create-listing" className="block py-2 text-sm font-semibold" style={{ color: C.primary }} onClick={() => setMobileOpen(false)}>{labels.rent}</Link>
              <button onClick={handleLogout} className="block py-2 text-sm font-medium text-red-500">{labels.logout}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-2 text-sm font-medium" style={{ color: C.dark }} onClick={() => setMobileOpen(false)}>{labels.login}</Link>
              <Link to="/register" className="block py-2 text-sm font-semibold" style={{ color: C.primary }} onClick={() => setMobileOpen(false)}>{labels.register}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
